import './polyfills'

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import {
  AppLocale,
  isAppLocale,
  LocalePreference,
  SYSTEM_LOCALE_PREFERENCE
} from '@common/config/localization/constants'
import {
  applyLocale,
  initI18nCore,
  resolveLocalePreference
} from '@common/config/localization/i18nCore'
import {
  readLocalePreference,
  subscribeToLocalePreference,
  writeLocalePreference
} from '@common/config/localization/localePreferenceStorage'
import { loadTranslations } from '@common/config/localization/loadTranslations'

initI18nCore({ loader: loadTranslations, plugins: [initReactI18next] })

// Line breaking for CJK, inherited by every text node (react-native-web sets no
// word-break/line-break). Korean breaks between words, not inside them; Japanese
// and Chinese keep closing punctuation and small kana off the start of a line.
// Rules go in through CSSOM, which the mobile-web CSP allows (a <style> with text
// would need 'unsafe-inline').
const CJK_LINE_BREAK_RULES = [
  'html:lang(ko) { word-break: keep-all; overflow-wrap: anywhere; }',
  'html:lang(ja), html:lang(zh) { line-break: strict; }'
]
let lineBreakRulesAdded = false
const addLineBreakRules = () => {
  if (lineBreakRulesAdded || typeof document === 'undefined' || !document.head) return
  lineBreakRulesAdded = true
  try {
    const style = document.createElement('style')
    document.head.appendChild(style)
    CJK_LINE_BREAK_RULES.forEach((rule) => style.sheet?.insertRule(rule))
  } catch {
    // Cosmetic only.
  }
}

const syncDocumentLang = (locale: string) => {
  if (typeof document === 'undefined') return
  // Lets the browser pick the right CJK glyph variants (zh-TW vs ja vs zh-CN)
  // and the matching system font (see FONT_FAMILIES).
  document.documentElement.lang = locale
  addLineBreakRules()
}
i18n.on('languageChanged', syncDocumentLang)
// initI18nCore() above may already have set the language.
if (i18n.language) syncDocumentLang(i18n.language)

/**
 * Resolves once the startup locale's translations are loaded. AppInit waits for
 * it (see useI18nReady) so non-English users never see an English first frame.
 */
const startupLocale = resolveLocalePreference(readLocalePreference())
let startupLocaleApplied = false
export const i18nReady: Promise<void> = applyLocale(startupLocale).finally(() => {
  startupLocaleApplied = true
})
export const isI18nReady = () => startupLocaleApplied || i18n.language === startupLocale

subscribeToLocalePreference((preference) => {
  applyLocale(resolveLocalePreference(preference)).catch(() => {})
})

export const getLocalePreference = (): LocalePreference => {
  const stored = readLocalePreference()
  return isAppLocale(stored) ? stored : SYSTEM_LOCALE_PREFERENCE
}

export const setLocalePreference = async (preference: LocalePreference) => {
  await writeLocalePreference(preference)
  await applyLocale(resolveLocalePreference(preference))
}

export const getCurrentLocale = () => i18n.language as AppLocale

export default i18n
export * from 'react-i18next'
